// A persistent Chat / Customize tab bar owns the working area on every screen.
// The wide-screen persona list stays visible in both tabs; only Customize has
// the resizable portrait preview. Tab switches leave the editor DOM untouched.

const CARD_KEY = "emailpa-persona-card-width"
const PANE_KEY = "emailpa-persona-right-pane"

// Superseded by PANE_KEY / the single grip. Cleared on sight so a returning
// user never carries a flag nothing reads any more — the head script used to
// turn emailpa-persona-form-hidden into html.persona-form-hidden, which today
// would hide nothing but would still sit in storage forever.
const LEGACY_KEYS = ["emailpa-persona-form-hidden", "emailpa-persona-chat-width"]

const PANE_CHAT = "chat"
const PANE_FORM = "form"

// Card min/max. Mirror the clamp in _persona.scss so JS and CSS agree. The
// ceiling is viewport-relative (same 60vw as sidebar_resize.js's maxWidth)
// rather than a fixed pixel cap, so the card can grow with the window.
const CARD_MIN = 260
const cardMax = () => Math.round(window.innerWidth * 0.6)
// The right-hand pane's floor, mirroring the grid's minmax(360px, 1fr).
const PANE_MIN = 360

const root = document.documentElement

const clampCard = (w) => Math.min(Math.max(w, CARD_MIN), cardMax())
const applyCard = (w) => root.style.setProperty("--persona-card-width", `${w}px`)

const read = (key) => {
    try {
        return localStorage.getItem(key)
    } catch {
        return null
    }
}

const write = (key, value) => {
    try {
        localStorage.setItem(key, value)
    } catch {
        // ignore storage errors
    }
}

const drop = (key) => {
    try {
        localStorage.removeItem(key)
    } catch {
        // ignore storage errors
    }
}

// The persona two-pane row, or null on every other page.
const personaRow = () => {
    const card = document.querySelector(".assistant-phone .detail-sidebar--persona")
    return card ? card.closest(".assistant-phone") : null
}

const paneCard = (row) => row.querySelector(".detail-sidebar--persona")

// --- Legacy cleanup (one pass per load; cheap and idempotent) ---
const dropLegacy = () => {
    LEGACY_KEYS.forEach(drop)
    // Defensive: nothing sets this class any more, but a page served from the
    // Turbo cache with the old head script could still carry it, and the CSS
    // that acted on it is gone — so it would be an invisible stuck state.
    root.classList.remove("persona-form-hidden")
}

// --- Persisted card width restore (also done pre-paint by the head script) ---
const restoreWidth = () => {
    const kw = parseInt(read(CARD_KEY), 10)
    if (!kw) return
    const row = personaRow()
    const card = row && paneCard(row)
    const rect = card?.getBoundingClientRect()
    const ceiling = rect?.width ? Math.max(CARD_MIN, window.innerWidth - rect.left - PANE_MIN - 16) : cardMax()
    applyCard(Math.min(clampCard(kw), ceiling))
}

// --- The pane switch ---
const syncPaneControls = (pane) => {
    document.querySelectorAll("[data-persona-mode]").forEach((button) => {
        const active = button.dataset.personaMode === pane
        button.classList.toggle("is-active", active)
        button.setAttribute("aria-selected", String(active))
        button.tabIndex = active ? 0 : -1
    })
    document.querySelectorAll("[data-persona-pane]").forEach((input) => {
        input.checked = input.dataset.personaPane === pane
    })
}

const applyPane = (pane, { persist = true } = {}) => {
    const toolbar = document.querySelector(".persona-workspace-toolbar")
    if (!toolbar) return
    if (toolbar.dataset.personaDraft === "true") pane = PANE_FORM
    root.classList.toggle("persona-pane-chat", pane === PANE_CHAT)
    if (persist) {
        write(PANE_KEY, pane)
        const url = new URL(window.location.href)
        url.searchParams.set("pane", pane)
        window.history.replaceState(window.history.state, "", url)
    }
    syncPaneControls(pane)
    restoreWidth()
}

document.addEventListener("persona:open-chat", () => applyPane(PANE_CHAT))

document.addEventListener("change", (event) => {
    const input = event.target.closest?.("[data-persona-pane]")
    if (!input || !input.checked) return
    applyPane(input.dataset.personaPane === PANE_FORM ? PANE_FORM : PANE_CHAT)
})

document.addEventListener("click", (event) => {
    const button = event.target.closest?.("[data-persona-mode]")
    if (!button) return
    if (button.disabled) return
    const pane = button.dataset.personaMode
    applyPane(pane)
})

// Native tab keyboard navigation, including a disabled Chat tab on drafts.
document.addEventListener("keydown", (event) => {
    if (!event.target.matches?.("[data-persona-mode]")) return
    const tabs = [...document.querySelectorAll("[data-persona-mode]:not(:disabled)")]
    const index = tabs.indexOf(event.target)
    let next
    if (event.key === "ArrowRight") next = tabs[(index + 1) % tabs.length]
    if (event.key === "ArrowLeft") next = tabs[(index - 1 + tabs.length) % tabs.length]
    if (event.key === "Home") next = tabs[0]
    if (event.key === "End") next = tabs[tabs.length - 1]
    if (!next) return
    event.preventDefault()
    applyPane(next.dataset.personaMode)
    next.focus()
})

// --- Grip ---
const makeGrip = () => {
    const grip = document.createElement("div")
    grip.className = "persona-grip persona-grip--card-pane"
    grip.dataset.personaGrip = "card-pane"
    grip.setAttribute("aria-hidden", "true")
    grip.dataset.tip = "Drag to resize"
    const bar = document.createElement("span")
    bar.className = "persona-grip-bar"
    grip.appendChild(bar)
    return grip
}

// Inject the grip as a direct child of .assistant-phone. Its grid placement is
// set in CSS, so the appended position in the DOM does not matter. Idempotent
// across Turbo loads and re-renders.
const ensureGrip = () => {
    const row = personaRow()
    if (!row) return
    if (!row.querySelector(":scope > .persona-grip--card-pane")) row.appendChild(makeGrip())
}

let dragging = false

const onGripMove = (event) => {
    const row = personaRow()
    if (!row) return
    const card = paneCard(row)
    if (!card) return
    // Resize the card by its left edge, but never push the right-hand pane
    // below its floor — the grid would otherwise overflow the row.
    const left = card.getBoundingClientRect().left
    const ceiling = Math.max(CARD_MIN, window.innerWidth - left - PANE_MIN)
    applyCard(Math.min(clampCard(event.clientX - left), ceiling))
}

const persistWidth = () => {
    const cardPx = parseInt(root.style.getPropertyValue("--persona-card-width"), 10)
    if (cardPx) write(CARD_KEY, String(cardPx))
}

const endDrag = () => {
    if (!dragging) return
    dragging = false
    root.classList.remove("persona-resizing")
    persistWidth()
}

document.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return
    const grip = event.target.closest?.(".persona-grip")
    if (!grip || !personaRow()) return
    dragging = true
    event.preventDefault()
    root.classList.add("persona-resizing")
})

document.addEventListener("pointermove", (event) => {
    if (!dragging) return
    onGripMove(event)
})

document.addEventListener("pointerup", endDrag)
document.addEventListener("pointercancel", endDrag)

// Double-click the grip resets the card to its default (clears the override).
document.addEventListener("dblclick", (event) => {
    const grip = event.target.closest?.(".persona-grip")
    if (!grip || !personaRow()) return
    root.style.removeProperty("--persona-card-width")
    drop(CARD_KEY)
})

// Apply the persisted state on every load. The head script sets the pane class
// pre-paint on a FULL page load, but Turbo navigations don't re-run head
// scripts — so on persona we (re)apply from localStorage here, and on every
// other page we leave the (inert, page-scoped) class alone.
// A ?pane= on the URL wins over the stored preference for this visit AND is
// persisted, because it only ever arrives from a redirect the user's own action
// caused: creating a persona, or forking one with "Save as new". Landing those
// on the chat pane would hide the very form they were just told to fill in.
const requestedPane = () => {
    try {
        const value = new URLSearchParams(window.location.search).get("pane")
        return value === PANE_FORM || value === PANE_CHAT ? value : null
    } catch {
        return null
    }
}

const syncOnLoad = () => {
    dropLegacy()
    if (!personaRow()) return
    ensureGrip()
    // First visit (or storage cleared): chat.
    const asked = requestedPane()
    if (asked) applyPane(asked)
    else applyPane(read(PANE_KEY) === PANE_FORM ? PANE_FORM : PANE_CHAT, { persist: false })
    restoreWidth()
}

window.addEventListener("resize", () => { if (personaRow()) restoreWidth() })
document.addEventListener("turbo:load", syncOnLoad)
if (document.readyState !== "loading") syncOnLoad()
else document.addEventListener("DOMContentLoaded", syncOnLoad)
